import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, apiFetch } from '../App';
import useFingerprint from '../hooks/useFingerprint';
import { Shield, CheckCircle, Loader, Eye, EyeOff } from 'lucide-react';

function getPasswordStrength(pwd) {
  if (!pwd) return null;
  let score = 0;
  const checks = {
    length:    pwd.length >= 8,
    uppercase: /[A-Z]/.test(pwd),
    lowercase: /[a-z]/.test(pwd),
    number:    /[0-9]/.test(pwd),
    special:   /[^A-Za-z0-9]/.test(pwd),
    long:      pwd.length >= 12,
  };
  score = Object.values(checks).filter(Boolean).length;
  if (score <= 2) return { level:'Weak',   color:'#ff4444', width:'25%',  checks };
  if (score <= 3) return { level:'Fair',   color:'#f5c842', width:'50%',  checks };
  if (score <= 4) return { level:'Good',   color:'#0e8fff', width:'75%',  checks };
  return              { level:'Strong', color:'#00c566', width:'100%', checks };
}

function PasswordStrength({ password, show }) {
  if (!show || !password) return null;
  const s = getPasswordStrength(password);
  if (!s) return null;
  return (
    <div style={{ marginBottom:16 }}>
      <div style={{ height:4, background:'var(--bg3)', borderRadius:4, overflow:'hidden', marginBottom:6 }}>
        <div style={{ height:'100%', width:s.width, background:s.color, borderRadius:4, transition:'width .3s, background .3s' }}/>
      </div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
        <span style={{ fontSize:11, fontWeight:700, color:s.color }}>{s.level} password</span>
        <span style={{ fontSize:10, color:'var(--muted)' }}>{password.length} characters</span>
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'4px 12px' }}>
        {[
          ['length',    '8+ characters'],
          ['uppercase', 'Uppercase letter'],
          ['lowercase', 'Lowercase letter'],
          ['number',    'Number'],
          ['special',   'Special (!@#$)'],
          ['long',      '12+ chars (bonus)'],
        ].map(([k,label]) => (
          <div key={k} style={{ display:'flex', alignItems:'center', gap:5, fontSize:11, color:s.checks[k]?'#00c566':'var(--muted)' }}>
            <span>{s.checks[k]?'✅':'○'}</span>
            <span>{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AuthPage() {
  const { login, showToast } = useAuth();
  const { fingerprint, components } = useFingerprint();
  const navigate = useNavigate();
  const [mode, setMode]           = useState('login');
  const [email, setEmail]         = useState('');
  const [password, setPassword]   = useState('');
  const [firstName, setFirstName] = useState('');
  const [loading, setLoading]     = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [showPwd, setShowPwd]       = useState(false);
  const params = new URLSearchParams(window.location.search);
  const verified = params.get('verified');

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    try {
      // Check device fingerprint before registration
      if (mode === 'register' && fingerprint) {
        const fpCheck = await apiFetch('/api/web/auth/check-fingerprint', {
          method: 'POST',
          body: { fingerprint, components }
        });
        if (!fpCheck.allowed) {
          const msg = fpCheck.existingEmail
            ? `${fpCheck.reason} (${fpCheck.existingEmail})`
            : fpCheck.reason;
          showToast(msg, 'error');
          setLoading(false);
          return;
        }
      }
      const endpoint = mode === 'login' ? '/api/web/auth/email-login' : '/api/web/auth/email-register';
      const body = mode === 'login' ? { email, password } : { email, password, firstName };
      const data = await apiFetch(endpoint, { method:'POST', body });
      if (mode === 'register' && data.emailSent) {
        setEmailSent(true);
        return;
      }
      login(data.token, data.user);
      // Save fingerprint linked to this account
      if (fingerprint) {
        apiFetch('/api/web/auth/save-fingerprint', {
          method: 'POST',
          body: { fingerprint, components }
        }).catch(() => {});
      }
      showToast(mode === 'login' ? 'Welcome back! 🎉' : 'Account created! 100 ⭐ Stars added! 🎉', 'success');
      navigate('/');
    } catch(err) {
      if (err.message === 'email_not_verified') {
        setEmailSent(true); // show "check your email" screen
      } else {
        showToast(err.message, 'error');
      }
    }
    setLoading(false);
  }

  async function resendVerification() {
    try {
      await apiFetch('/api/web/auth/resend-verify', { method:'POST', body:{ email }});
      showToast('Verification email resent!', 'success');
    } catch(err) { showToast(err.message, 'error'); }
  }

  // Telegram Login Widget
  useEffect(() => {
    window.onTelegramAuth = async (tgUser) => {
      try {
        const data = await apiFetch('/api/web/auth/telegram', { method:'POST', body: tgUser });
        login(data.token, data.user);
        if (fingerprint) {
          apiFetch('/api/web/auth/save-fingerprint', {
            method: 'POST',
            body: { fingerprint, components }
          }).catch(() => {});
        }
        // loadMe will trigger NIN gate if not verified
        showToast('Welcome! 🎉', 'success');
        navigate('/');
      } catch(err) { showToast(err.message, 'error'); }
    };
    const el = document.getElementById('tg-login-widget');
    if (el && !el.hasChildNodes()) {
      const script = document.createElement('script');
      script.src = 'https://telegram.org/js/telegram-widget.js?22';
      script.setAttribute('data-telegram-login', 'NaijaPredict_Bot');
      script.setAttribute('data-size', 'large');
      script.setAttribute('data-onauth', 'onTelegramAuth(user)');
      script.setAttribute('data-request-access', 'write');
      script.setAttribute('data-userpic', 'false');
      script.async = true;
      el.appendChild(script);
    }
    return () => { delete window.onTelegramAuth; };
  }, []);

  return (
    <div style={{ minHeight:'100vh', background:'var(--bg)', display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
      <div style={{ width:'100%', maxWidth:420 }}>

        {/* Logo */}
        <div style={{ textAlign:'center', marginBottom:32 }}>
          <img src="/logo.png" alt="NaijaPredict" style={{ width:72, height:72, borderRadius:16, marginBottom:12, display:'block', margin:'0 auto 12px' }}/>
          <div className="brand-text" style={{ fontSize:36 }}>NAIJAPREDICT</div>
          <div style={{ fontSize:13, color:'var(--text2)', marginTop:6 }}>Nigeria's #1 Skill-Based Prediction Platform</div>
        </div>

        {verified==='1' && (
          <div style={{background:'rgba(0,197,102,.1)',border:'1px solid #00c566',borderRadius:12,padding:'12px 16px',marginBottom:16,fontSize:13,color:'#00c566',textAlign:'center'}}>
            ✅ Email verified successfully! You can now sign in.
          </div>
        )}
        {verified==='0' && (
          <div style={{background:'rgba(255,68,68,.1)',border:'1px solid #ff4444',borderRadius:12,padding:'12px 16px',marginBottom:16,fontSize:13,color:'#ff4444',textAlign:'center'}}>
            ❌ Verification link expired. Please register again.
          </div>
        )}
        {emailSent ? (
          <div className="card" style={{textAlign:'center',padding:32}}>
            <div style={{fontSize:48,marginBottom:16}}>📧</div>
            <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:22,letterSpacing:1,marginBottom:8}}>CHECK YOUR EMAIL</div>
            <div style={{fontSize:13,color:'var(--text2)',marginBottom:20,lineHeight:1.7}}>
              We sent a verification link to <strong style={{color:'var(--text)'}}>{email}</strong>.<br/>
              Click the link to activate your account and claim your 100 ⭐ Stars!
            </div>
            <div style={{fontSize:12,color:'var(--muted)'}}>Didn't receive it? Check spam folder or <button onClick={()=>setEmailSent(false)} style={{background:'none',border:'none',color:'var(--orange)',cursor:'pointer',fontSize:12,textDecoration:'underline'}}>try again</button></div>
          </div>
        ) : (
        <div className="card">

          {/* Tabs */}
          <div style={{ display:'flex', background:'var(--bg2)', borderRadius:10, padding:4, marginBottom:24 }}>
            {[['login','Sign In'],['register','Register']].map(([m,l]) => (
              <button key={m} onClick={() => setMode(m)} style={{
                flex:1, padding:'8px', borderRadius:8, border:'none', cursor:'pointer', fontSize:13, fontWeight:600, transition:'all .15s',
                background: mode===m ? 'var(--bg1)' : 'transparent',
                color: mode===m ? 'var(--text)' : 'var(--text2)',
                boxShadow: mode===m ? '0 1px 4px rgba(0,0,0,.3)' : 'none',
              }}>{l}</button>
            ))}
          </div>

          {/* Email/Password form */}
          <form onSubmit={handleSubmit}>
            {mode === 'register' && (
              <>
                <label style={{ fontSize:11, color:'var(--text2)', fontWeight:600, letterSpacing:.5 }}>YOUR NAME</label>
                <input type="text" placeholder="e.g. Emeka" value={firstName} onChange={e=>setFirstName(e.target.value)}
                  style={{ width:'100%', marginTop:6, marginBottom:14, padding:'11px 14px', background:'var(--bg2)', border:'1px solid var(--border)', borderRadius:10, color:'var(--text)', fontSize:14, outline:'none' }}/>
              </>
            )}
            <label style={{ fontSize:11, color:'var(--text2)', fontWeight:600, letterSpacing:.5 }}>EMAIL ADDRESS</label>
            <input type="email" placeholder="you@example.com" value={email} onChange={e=>setEmail(e.target.value)} required
              style={{ width:'100%', marginTop:6, marginBottom:14, padding:'11px 14px', background:'var(--bg2)', border:'1px solid var(--border)', borderRadius:10, color:'var(--text)', fontSize:14, outline:'none' }}/>
            <label style={{ fontSize:11, color:'var(--text2)', fontWeight:600, letterSpacing:.5 }}>PASSWORD</label>
            <div style={{ position:'relative', marginTop:6, marginBottom: mode==='register' && password ? 8 : 20 }}>
              <input type={showPwd?'text':'password'} placeholder="Min 6 characters" value={password} onChange={e=>setPassword(e.target.value)} required minLength={6}
                style={{ width:'100%', padding:'11px 40px 11px 14px', background:'var(--bg2)', border:`1px solid ${mode==='register'&&password?(getPasswordStrength(password)?.color||'var(--border)'):'var(--border)'}`, borderRadius:10, color:'var(--text)', fontSize:14, outline:'none' }}/>
              <button type="button" onClick={()=>setShowPwd(p=>!p)}
                style={{ position:'absolute', right:10, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', fontSize:16, color:'var(--muted)' }}>
                {showPwd ? '🙈' : '👁️'}
              </button>
            </div>
            <PasswordStrength password={password} show={mode==='register'}/>
            <button className="btn-primary" type="submit" disabled={loading} style={{ width:'100%', padding:13, fontSize:15 }}>
              {loading ? 'Please wait...' : mode === 'login' ? 'Sign In →' : 'Create Account →'}
            </button>
          </form>

          {mode === 'register' && (
            <div style={{ marginTop:14, padding:'10px 14px', background:'rgba(0,197,102,.06)', border:'1px solid rgba(0,197,102,.2)', borderRadius:10, fontSize:12, color:'#00c566', textAlign:'center' }}>
              🎁 New accounts get <strong>100 ⭐ Stars free</strong> — verify your NIN to unlock withdrawals!
            </div>
          )}

          {/* Divider */}
          <div style={{ display:'flex', alignItems:'center', gap:12, margin:'20px 0' }}>
            <div style={{ flex:1, height:1, background:'var(--border)' }}/>
            <div style={{ fontSize:12, color:'var(--muted)' }}>OR CONTINUE WITH</div>
            <div style={{ flex:1, height:1, background:'var(--border)' }}/>
          </div>

          {/* Telegram Login Widget */}
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            <div style={{ display:'flex', justifyContent:'center' }} id="tg-login-widget"/>
            <div style={{ fontSize:11, color:'var(--muted)', textAlign:'center' }}>
              Link your existing Telegram account to NaijaPredict
            </div>

            {/* Divider */}
            <div style={{ display:'flex', alignItems:'center', gap:12, margin:'4px 0' }}>
              <div style={{ flex:1, height:1, background:'var(--border)' }}/>
              <div style={{ fontSize:11, color:'var(--muted)' }}>or</div>
              <div style={{ flex:1, height:1, background:'var(--border)' }}/>
            </div>

            {/* Open in Telegram app */}
            <div style={{ background:'rgba(41,182,246,.06)', border:'1px solid rgba(41,182,246,.2)', borderRadius:12, padding:'14px 16px', textAlign:'center' }}>
              <div style={{ fontSize:13, fontWeight:600, color:'#29b6f6', marginBottom:4 }}>📱 Already using the Telegram Bot?</div>
              <div style={{ fontSize:12, color:'var(--text2)', marginBottom:12 }}>
                Open <strong>@NaijaPredict_Bot</strong> on Telegram to stake, withdraw and manage your account.
              </div>
              <div style={{ display:'flex', gap:8, justifyContent:'center', flexWrap:'wrap' }}>
                <a href="https://telegram.me/NaijaPredict_Bot" target="_blank" rel="noreferrer"
                  style={{ padding:'8px 16px', background:'#29b6f6', borderRadius:8, color:'#000', fontSize:12, fontWeight:700, textDecoration:'none', display:'inline-flex', alignItems:'center', gap:6 }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/></svg>
                  Open on Telegram
                </a>
                <a href="https://web.telegram.org/k/#@NaijaPredict_Bot" target="_blank" rel="noreferrer"
                  style={{ padding:'8px 16px', background:'rgba(41,182,246,.1)', border:'1px solid rgba(41,182,246,.3)', borderRadius:8, color:'#29b6f6', fontSize:12, fontWeight:600, textDecoration:'none' }}>
                  Open Web Telegram
                </a>
              </div>
            </div>
          </div>
        </div>

        )}
        {/* Perks */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:10, marginTop:16 }}>
          {[['⭐','100 Stars Free','On signup'],['💳','Top up','From ₦500'],['🏦','Withdraw','To your bank']].map(([icon,title,sub]) => (
            <div key={title} style={{ textAlign:'center', background:'var(--bg1)', border:'1px solid var(--border)', borderRadius:12, padding:'12px 8px' }}>
              <div style={{ fontSize:22, marginBottom:4 }}>{icon}</div>
              <div style={{ fontSize:12, fontWeight:700 }}>{title}</div>
              <div style={{ fontSize:10, color:'var(--muted)' }}>{sub}</div>
            </div>
          ))}
        </div>

        {/* Legal links */}
        <div style={{ textAlign:'center', marginTop:16, fontSize:11, color:'var(--muted)' }}>
          By signing up, you agree to our{' '}
          <a href="/terms" style={{ color:'var(--orange)', textDecoration:'underline' }}>Terms & Conditions</a>
          {' '}and{' '}
          <a href="/privacy" style={{ color:'var(--orange)', textDecoration:'underline' }}>Privacy Policy</a>
        </div>
      </div>
    </div>
  );
}
